import type { AssetResponse, AssetUpdateFields } from '../../api';
import {
  emptyMetadataValue,
  METADATA_FIELD_LABELS,
  type MetadataFieldKey,
  type MetadataFormValue,
} from '../../constants/metadataFields';

/**
 * The shared-value logic behind the details drawer.
 *
 * Pure and separate from the component because this is where bulk edit is
 * actually decided: which fields show a value, which show "Mixed", and which
 * end up in the PATCH body. That is worth testing directly rather than through
 * a rendered form.
 */

/**
 * The asset fields the bulk editor may change (`AssetUpdateFields`).
 *
 * `asset_type` and `source_image_code` are absent on purpose: the kind is fixed
 * at upload, and a source image's code is allocated there. `source_image_ids`
 * is absent too —
 * it needs a picker of existing source images, not a text box.
 */
export const EDITABLE_FIELDS = [
  'nameplate',
  'business_unit',
  'carline_code',
  'tier',
  'ai_indicator',
  'source_agency',
  'domain',
  'is_lifestyle_image',
  'expiry_rights',
  'copy_elements',
  'platform_rights',
  'channel_rights',
  'country_rights',
  'format_type',
  'format_size',
  'duration_seconds',
  'legacy_fcv_id',
  'campaign_name',
  'creative_variation',
  'production_ticket_ref',
  'briefing_form_ref',
  'notes',
] as const satisfies readonly MetadataFieldKey[];

export type EditableField = (typeof EDITABLE_FIELDS)[number];

/** The drawer's form: one metadata value plus which fields are mixed. */
export interface FormState {
  value: MetadataFormValue;
  mixed: Set<EditableField>;
}

/**
 * Stable serialisation for cross-asset comparison. Lists compare by content —
 * copy rows in order — so two assets with the same values still read as shared.
 */
function fingerprint(asset: AssetResponse, field: EditableField): string {
  switch (field) {
    case 'copy_elements':
      return JSON.stringify(asset.copy_elements ?? []);
    case 'nameplate':
      return JSON.stringify([...(asset.nameplate ?? [])].sort());
    case 'carline_code':
      return JSON.stringify([...(asset.carline_code ?? [])].sort());
    case 'business_unit':
      return JSON.stringify([...(asset.business_unit ?? [])].sort());
    case 'tier':
      return JSON.stringify([...(asset.tier ?? [])].sort());
    case 'platform_rights':
      return JSON.stringify([...(asset.platform_rights ?? [])].sort());
    case 'channel_rights':
      return JSON.stringify([...(asset.channel_rights ?? [])].sort());
    case 'country_rights':
      return JSON.stringify([...(asset.country_rights ?? [])].sort());
    case 'is_lifestyle_image':
      return String(asset.is_lifestyle_image ?? false);
    case 'duration_seconds':
      return String(asset.duration_seconds ?? '');
    default:
      return asset[field] ?? '';
  }
}

function sharedValue(asset: AssetResponse, field: EditableField): Partial<MetadataFormValue> {
  switch (field) {
    case 'copy_elements':
      return { copy_elements: asset.copy_elements ?? [] };
    case 'nameplate':
      return { nameplate: asset.nameplate ?? [] };
    case 'carline_code':
      return { carline_code: asset.carline_code ?? [] };
    case 'business_unit':
      return { business_unit: asset.business_unit ?? [] };
    case 'tier':
      return { tier: asset.tier ?? [] };
    case 'platform_rights':
      return { platform_rights: asset.platform_rights ?? [] };
    case 'channel_rights':
      return { channel_rights: asset.channel_rights ?? [] };
    case 'country_rights':
      return { country_rights: asset.country_rights ?? [] };
    case 'is_lifestyle_image':
      return { is_lifestyle_image: asset.is_lifestyle_image ?? false };
    case 'duration_seconds':
      return { duration_seconds: String(asset.duration_seconds ?? '') };
    default:
      return { [field]: asset[field] ?? '' };
  }
}

/**
 * Collapse a selection into one form state.
 *
 * A field is shared when every selected asset agrees on it, and mixed
 * otherwise. A single selection is just the degenerate case where everything
 * agrees, which is why one component serves both — there is no separate
 * "single asset" mode to maintain. A mixed field starts empty: whatever the
 * user then puts in it replaces every selected asset's value.
 */
export function deriveFormState(assets: AssetResponse[]): FormState {
  const value = emptyMetadataValue();
  const mixed = new Set<EditableField>();

  if (assets.length === 0) return { value, mixed };

  for (const field of EDITABLE_FIELDS) {
    const first = fingerprint(assets[0], field);
    const allAgree = assets.every((asset) => fingerprint(asset, field) === first);
    if (allAgree) {
      Object.assign(value, sharedValue(assets[0], field));
    } else {
      mixed.add(field);
    }
  }

  return { value, mixed };
}

/**
 * Take fresh server values for every field except the ones being edited.
 *
 * The drawer's rows can change underneath it while it is open — linking a key
 * visual copies the hero's metadata onto the very assets on screen. Re-deriving
 * the whole form would yank a half-typed field out from under the user;
 * re-deriving none of it, which is what a single "is anything dirty?" guard
 * amounts to, leaves *every other field* showing a value the row no longer
 * has, with nothing on screen to say so and no way to fix it short of
 * reselecting. Neither is acceptable, so the choice is made per field.
 *
 * A dirty field is never mixed — the user replaced it across the selection —
 * so keeping its value means dropping it from the incoming mixed set too.
 */
export function mergeFormState(
  current: FormState,
  fresh: FormState,
  dirty: Set<EditableField>,
): FormState {
  if (dirty.size === 0) return fresh;

  const value = { ...fresh.value };
  const mixed = new Set(fresh.mixed);
  for (const field of dirty) {
    Object.assign(value, { [field]: current.value[field] });
    mixed.delete(field);
  }
  return { value, mixed };
}

/**
 * Build the PATCH body from the fields the user actually touched.
 *
 * Only dirty fields are included. This is the whole reason the backend applies
 * `exclude_unset`: a field left out of the body is left out of the UPDATE,
 * whereas sending it as null would wipe that column on every selected row.
 *
 * Most fields here are publish-required and the backend refuses to clear any
 * of them, so an emptied field is dropped rather than sent. The optional
 * fields with an explicit clear are handled case by case below. Copy rows
 * with blank text are trimmed out for the same reason the backend requires
 * non-empty values.
 */
export function buildUpdateFields(state: FormState, dirty: Set<EditableField>): AssetUpdateFields {
  const fields: AssetUpdateFields = {};
  const { value } = state;

  for (const field of dirty) {
    switch (field) {
      case 'copy_elements': {
        // Sent even when empty: copy is optional to publish, so removing the
        // last row is a real edit — "this asset carries no copy" — not a clear
        // the guard has to refuse.
        fields.copy_elements = value.copy_elements
          .map((row) => ({ type: row.type, value: row.value.trim() }))
          .filter((row) => row.value !== '');
        break;
      }
      // Publish-required of every kind, so clear-guarded: an emptied list is
      // dropped, like `tier`.
      case 'nameplate':
        if (value.nameplate.length > 0) fields.nameplate = value.nameplate;
        break;
      case 'carline_code':
        if (value.carline_code.length > 0) fields.carline_code = value.carline_code;
        break;
      case 'business_unit':
        if (value.business_unit.length > 0) fields.business_unit = value.business_unit;
        break;
      case 'tier':
        if (value.tier.length > 0) fields.tier = value.tier;
        break;
      case 'platform_rights':
        if (value.platform_rights.length > 0) fields.platform_rights = value.platform_rights;
        break;
      // Publish-required of every kind (COMMON_PUBLISH_FIELDS), so in the
      // backend's clear guard: an emptied list is dropped. `domain` is too, and
      // the default branch drops it the same way.
      case 'channel_rights':
        if (value.channel_rights.length > 0) fields.channel_rights = value.channel_rights;
        break;
      case 'country_rights':
        if (value.country_rights.length > 0) fields.country_rights = value.country_rights;
        break;
      // A master's are always sent as a pair: the backend refuses half of
      // one, since it would meet whatever half each selected row already
      // stores. A film has no type, so its size (its aspect ratio) goes alone.
      // Publish-required of both, so a blank is dropped, like `tier`.
      case 'format_type':
      case 'format_size':
        if (value.format_type && value.format_size) {
          fields.format_type = value.format_type;
          fields.format_size = value.format_size;
        } else if (!value.format_type && value.format_size) {
          fields.format_size = value.format_size;
        }
        break;
      // Publish-required of films, so clear-guarded: a blank is dropped.
      case 'duration_seconds':
        if (value.duration_seconds) fields.duration_seconds = Number(value.duration_seconds);
        break;
      case 'is_lifestyle_image':
        // Always sent: unchecking is a real edit, and false is never a clear.
        fields.is_lifestyle_image = value.is_lifestyle_image;
        break;
      case 'legacy_fcv_id': {
        // Optional traceability field, not publish-required and not in the
        // backend's clear guard: emptying it sends an explicit null so the
        // column is wiped rather than silently left as-is.
        const trimmed = value.legacy_fcv_id.trim();
        fields.legacy_fcv_id = trimmed === '' ? null : trimmed;
        break;
      }
      default: {
        const trimmed = value[field].trim();
        if (trimmed !== '') fields[field] = trimmed;
      }
    }
  }

  return fields;
}

/** True when there is at least one change worth sending. */
export function hasChanges(state: FormState, dirty: Set<EditableField>): boolean {
  return Object.keys(buildUpdateFields(state, dirty)).length > 0;
}

/**
 * Fields the user edited that `buildUpdateFields` refuses to serialize.
 *
 * Emptying a publish-required field is a real edit with no representation in
 * the PATCH body: the backend will not clear those columns, so the value is
 * dropped. The form must not treat that as "nothing happened". If it does, the
 * rail shows a blank nameplate, `hasChanges` reports false, and the overlay's
 * Save & publish skips straight to publishing the stale server value — the one
 * thing the user just tried to remove.
 *
 * Returned as labels rather than a boolean so the refusal can name the field
 * that needs attention; "some field is empty" sends people hunting through a
 * rail of twenty inputs.
 */
export function getUnsavableClears(state: FormState, dirty: Set<EditableField>): string[] {
  const serialized = buildUpdateFields(state, dirty);
  return [...dirty]
    .filter((field) => !(field in serialized))
    .map((field) => METADATA_FIELD_LABELS[field] ?? field);
}
